import type {
  AiAgentMetadata,
  AiAgentRequest,
  AiAgentSource,
  AiAgentStreamEvent,
  AiAgentStreamResult,
} from '../types';

const aiAgentChatEndpoint = `${process.env.AI_AGENT_API_BASE_URL ?? ''}/api/v1/chat/stream`;

export const maxFetchRetries = 2;

interface StreamAiAgentResponseProps {
  message: string;
  onChunk: (accumulatedResponse: string, sources: AiAgentSource[]) => void;
  sessionId?: string;
}

/**
 * Streams an AI agent chat response via SSE (Server-Sent Events), invoking a callback
 * with the accumulated response text and sources as each chunk arrives.
 *
 * @param params - Stream request parameters.
 * @param params.message - The user's message to send to the AI agent.
 * @param params.onChunk - Called after each SSE chunk with the full accumulated response so far
 *   and the latest sources array.
 * @param params.sessionId - Optional unique identifier for the current chat session.
 *   If omitted, the server auto-generates one returned via the resolved result.
 * @returns Metadata from the completed stream (session ID, suggested prompts, etc.).
 * @throws When the HTTP request fails (after retries) or the server sends an error event.
 */
export const streamAiAgentResponse = async ({
  message,
  onChunk,
  sessionId,
}: StreamAiAgentResponseProps): Promise<AiAgentStreamResult> => {
  const requestInit: RequestInit = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      message: message.trim(),
      session_id: sessionId,
    } satisfies AiAgentRequest),
  };

  let response: Response | undefined;
  let lastFetchError: unknown;

  for (let attempt = 0; attempt <= maxFetchRetries; attempt++) {
    try {
      const currentResponse = await fetch(aiAgentChatEndpoint, requestInit);

      if (!currentResponse.ok || !currentResponse.body) {
        void currentResponse.body?.cancel()?.catch(() => {});
        throw new Error(`Request failed: ${currentResponse.status}`);
      }

      response = currentResponse;
      break;
    } catch (error) {
      lastFetchError = error;
    }
  }

  if (!response?.body) {
    throw lastFetchError;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let isStreamDone = false;
  let accumulatedResponse = '';
  const accumulatedSources: AiAgentSource[] = [];
  let metadata: AiAgentMetadata | undefined;

  // Buffer holds incomplete event data between read() calls,
  // since a read() can split an event across two chunks
  let buffer = '';

  try {
    while (!isStreamDone) {
      const result = await reader.read();
      isStreamDone = result.done;

      if (!result.value) break;

      // Append decoded bytes to the buffer
      buffer += decoder.decode(result.value, { stream: true });

      // SSE events are separated by a blank line. The spec allows LF or CRLF
      // line terminators, so match both \n\n and \r\n\r\n (and mixed forms).
      const events = buffer.split(/\r?\n\r?\n/);

      // The last element after split is either:
      // - An empty string (buffer ended with \n\n, meaning all events are complete)
      // - An incomplete event fragment waiting for more data
      // Either way, we keep it in the buffer for the next iteration
      buffer = events.pop() ?? '';

      for (const event of events) {
        // Each SSE event can have multiple lines (e.g. "event: type\ndata: {...}").
        // We only care about the line starting with "data: " which holds the JSON payload
        // Lines like "event: connected" or "id: ..." are ignored here
        const dataLine = event.split(/\r?\n/).find(line => line.startsWith('data:'));

        // Skip events with no data line (e.g. the "event: connected" heartbeat
        // only has an event type and an empty data object we don't need)
        if (!dataLine) continue;

        // Strip the "data:" prefix, then remove the optional leading space per the SSE spec
        const json = dataLine.slice('data:'.length).trimStart();
        const parsed = JSON.parse(json) as AiAgentStreamEvent | Record<string, never>;

        // The `connected` event sends `{}` with no type field — skip it
        if (!('type' in parsed)) continue;

        switch (parsed.type) {
          case 'text':
            accumulatedResponse += parsed.content;
            onChunk(accumulatedResponse, accumulatedSources);
            break;

          case 'source':
            accumulatedSources.push(parsed.source);
            onChunk(accumulatedResponse, accumulatedSources);
            break;

          case 'metadata':
            metadata = parsed.metadata;
            break;

          case 'done':
            return buildStreamResult(metadata);

          case 'error':
            throw new Error(parsed.error);
        }
      }
    }
  } finally {
    void reader.cancel()?.catch(() => {});
  }

  if (!accumulatedResponse.trim()) {
    throw new Error('Stream completed without a response');
  }

  return buildStreamResult(metadata);
};

const buildStreamResult = (metadata?: AiAgentMetadata): AiAgentStreamResult => ({
  sessionId: metadata?.session_id,
  messageId: metadata?.message_id,
  suggestedPrompts: metadata?.suggested_prompts,
  confidence: metadata?.confidence,
});
